/**
 * Shared handling for the server's `ai_limit_reached` response (see
 * server/aiUsageLimiter.js), returned by any of the 5 AI endpoints once the
 * user's monthly AI credit budget is spent.
 *
 * Every AI call site already does `if (result.error) { setXError(result.error) }` —
 * this helper lets each of them special-case ai_limit_reached into the shared
 * upgrade-prompt UI instead of a generic inline error string, without duplicating
 * the check-and-dispatch logic at each of the 4 call sites.
 */
import { usePricingStore } from './pricingStore';
import { UpgradeMessageKey } from './upgradeMessages';

interface AiLimitReachedResult {
    error?: string;
    creditsUsed?: number;
    creditLimit?: number;
}

/**
 * If `result.error` is `'ai_limit_reached'`, shows the AI-usage upgrade prompt and
 * returns `true` — the caller should skip setting its own generic error message in
 * that case. Returns `false` for any other error (or no error), so it composes with
 * an existing `if (result.error) { ... }` branch without changing its shape:
 *
 *   if (result.error) {
 *       if (!handleAiLimitReached(result)) setError(result.error);
 *   }
 */
export function handleAiLimitReached(result: AiLimitReachedResult): boolean {
    if (result.error !== 'ai_limit_reached') return false;
    usePricingStore.getState().showUpgradePrompt(UpgradeMessageKey.AiUsageLimit, {
        creditsUsed: String(result.creditsUsed ?? ''),
        creditLimit: String(result.creditLimit ?? ''),
    });
    return true;
}
